// Job Work (subcontract) DC ↔ GIN progress. A DC can be received over any
// number of GINs, a part-quantity and/or a few lines at a time. The backend
// derives each DC line's progress from the GINs raised against it and
// stamps it on every subcontract DC it returns (see
// `_attach_dc_display_fields` / `_dc_receipt_state` in server.py):
//   line.received_qty — total received across every GIN
//   line.pending_qty  — sent qty − received_qty (what the next GIN may receive)
//   line.gin_codes    — every GIN the line was received on
// The DC stays open (dispatched) until every line's pending_qty is 0.

const num = (v) => Number(v || 0);

// Whether a DC line carries goods to receive back: a master item, or a
// manual (one-time) item typed in by hand (no item_id).
export const isReceivableDcLine = (line) => !!(line?.item_id || line?.is_manual);

// Qty of a DC line still to be received. Falls back to "fully received if
// it points at a GIN" only for a DC that came without derived fields.
export const dcLinePendingQty = (line) => {
  if (!isReceivableDcLine(line)) return 0;
  if (line.pending_qty != null) return num(line.pending_qty);
  return line.gin_id ? 0 : num(line.qty);
};

export const dcLineReceivedQty = (line) => num(line?.received_qty);

// True once a DC line's full qty has been received.
export const isDcLineReceived = (dc, line) => isReceivableDcLine(line) && dcLinePendingQty(line) <= 0;

// GIN code(s) a DC line was received on ("" while nothing has come back).
export const dcLineGinCode = (dc, line) =>
  (line?.gin_codes?.length ? line.gin_codes.join(", ") : line?.gin_code || "");

// Indexes of the DC lines with qty still to be received.
export const pendingDcLineIdxs = (dc) =>
  (dc?.lines || []).reduce(
    (acc, l, idx) => (isReceivableDcLine(l) && !isDcLineReceived(dc, l) ? [...acc, idx] : acc),
    []
  );

export const hasPendingGinLines = (dc) => pendingDcLineIdxs(dc).length > 0;

// Line counts ({ received, total } — lines fully received out of all item
// lines) plus qty totals across them.
export const dcGinProgress = (dc) => {
  const lines = (dc?.lines || []).filter(isReceivableDcLine);
  const sentQty = lines.reduce((s, l) => s + num(l.qty), 0);
  const pendingQty = lines.reduce((s, l) => s + dcLinePendingQty(l), 0);
  return {
    received: lines.filter((l) => isDcLineReceived(dc, l)).length,
    total: lines.length,
    sentQty,
    receivedQty: lines.reduce((s, l) => s + dcLineReceivedQty(l), 0),
    pendingQty,
    anyReceived: lines.some((l) => dcLineReceivedQty(l) > 0),
  };
};

// Trim float noise for display (e.g. 0.30000000000000004 → 0.3).
export const fmtQty = (v) => String(Math.round(num(v) * 1000) / 1000);
